import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getMonitoringStats,
  listMonitoredConversations,
  triggerSentimentBackfill,
} from '../../services/adminApi';
import useMonitoringWebSocket from '../hooks/useMonitoringWebSocket';
import './MonitoringDashboard.css';

const CHANNEL_LABELS = {
  whatsapp: 'WhatsApp',
  facebook_dm: 'Facebook DM',
  facebook_comment: 'Facebook commenti',
  instagram_dm: 'Instagram DM',
  instagram_comment: 'Instagram commenti',
};

function StatusBadge({ status }) {
  const label =
    status === 'open'
      ? 'Live'
      : status === 'connecting'
      ? 'Connessione…'
      : status === 'closed'
      ? 'Disconnesso'
      : status === 'error'
      ? 'Errore'
      : 'In attesa';
  return (
    <span className={`live-badge ${status}`}>
      <span className="dot" />
      {label}
    </span>
  );
}

export default function MonitoringDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [topProblematic, setTopProblematic] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillNotice, setBackfillNotice] = useState(null);
  // Used to bump a counter on every WS event so the dashboard reloads
  // its KPIs without forcing a polling interval.
  const [reloadToken, setReloadToken] = useState(0);

  const { status: wsStatus } = useMonitoringWebSocket(
    useCallback(() => {
      // Throttle reloads: schedule a refresh shortly after the latest
      // event so a burst of webhooks coalesces into a single fetch.
      setReloadToken((n) => n + 1);
    }, [])
  );

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, problematicRes] = await Promise.all([
        getMonitoringStats(),
        listMonitoredConversations({
          page: 1,
          page_size: 10,
          is_problematic: true,
        }),
      ]);
      setStats(statsRes.data);
      setTopProblematic(problematicRes.data?.items || []);
    } catch (err) {
      console.error('Monitoring dashboard load failed:', err);
      setError(
        err.response?.data?.detail ||
          'Impossibile caricare le statistiche di monitoraggio.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Debounced reload triggered by WS events.
  useEffect(() => {
    if (reloadToken === 0) return;
    const t = setTimeout(() => {
      loadAll();
    }, 1500);
    return () => clearTimeout(t);
  }, [reloadToken, loadAll]);

  const sentiment = stats?.sentiment_distribution || {};
  const totalSent =
    (sentiment.positive || 0) +
    (sentiment.neutral || 0) +
    (sentiment.negative || 0) +
    (sentiment.mixed || 0);

  const sentimentBar = useMemo(() => {
    if (!totalSent) return null;
    const pct = (n) => Math.max(0, Math.round((n / totalSent) * 100));
    return (
      <div className="sentiment-bar">
        {sentiment.positive ? (
          <div className="pos" style={{ width: `${pct(sentiment.positive)}%` }}>
            {pct(sentiment.positive)}%
          </div>
        ) : null}
        {sentiment.neutral ? (
          <div className="neu" style={{ width: `${pct(sentiment.neutral)}%` }}>
            {pct(sentiment.neutral)}%
          </div>
        ) : null}
        {sentiment.negative ? (
          <div className="neg" style={{ width: `${pct(sentiment.negative)}%` }}>
            {pct(sentiment.negative)}%
          </div>
        ) : null}
        {sentiment.mixed ? (
          <div className="mix" style={{ width: `${pct(sentiment.mixed)}%` }}>
            {pct(sentiment.mixed)}%
          </div>
        ) : null}
      </div>
    );
  }, [sentiment, totalSent]);

  const handleBackfill = async () => {
    setBackfillBusy(true);
    setBackfillNotice(null);
    try {
      const res = await triggerSentimentBackfill(500);
      setBackfillNotice(
        `Backfill avviato (task ${res.data?.task_id || '—'}). Aggiorna la pagina tra qualche minuto.`
      );
    } catch (err) {
      setBackfillNotice(
        err.response?.data?.detail ||
          'Impossibile avviare il backfill del sentiment.'
      );
    } finally {
      setBackfillBusy(false);
    }
  };

  return (
    <div className="monitoring-page">
      <h1>
        Monitoraggio conversazioni
        <StatusBadge status={wsStatus} />
      </h1>
      <div className="subtitle">
        Vista cross-tenant su WhatsApp, Facebook (DM e commenti) e Instagram
        (DM e commenti). Gli aggiornamenti arrivano in tempo reale via
        WebSocket.
      </div>

      {error && <div className="error-banner">{error}</div>}
      {backfillNotice && (
        <div className="error-banner" style={{ background: '#e0f2fe', color: '#075985' }}>
          {backfillNotice}
        </div>
      )}

      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="label">Conversazioni totali</div>
          <div className="value">{stats?.total_conversations ?? '—'}</div>
        </div>
        <div className="kpi-card warning">
          <div className="label">Problematiche (campione)</div>
          <div className="value">
            {stats?.problematic_in_sample ?? '—'}
            {stats?.sample_size ? (
              <span style={{ fontSize: 12, color: '#666', marginLeft: 6 }}>
                / {stats.sample_size}
              </span>
            ) : null}
          </div>
        </div>
        <div className="kpi-card">
          <div className="label">Escalation attive</div>
          <div className="value">{stats?.active_escalations ?? '—'}</div>
        </div>
        <div className="kpi-card alert">
          <div className="label">SLA superato</div>
          <div className="value">{stats?.sla_breached ?? '—'}</div>
        </div>
      </div>

      <div className="section">
        <h2>Distribuzione sentiment</h2>
        {totalSent > 0 ? sentimentBar : <div className="empty-state">Nessun dato sentiment nel periodo.</div>}
      </div>

      <div className="section">
        <h2>Conversazioni per canale</h2>
        {stats?.by_channel && Object.keys(stats.by_channel).length ? (
          <div className="channel-bar">
            {Object.entries(stats.by_channel).map(([ch, count]) => (
              <div className="channel-pill" key={ch}>
                {CHANNEL_LABELS[ch] || ch}
                <span className="count">{count}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">Nessuna conversazione nel periodo.</div>
        )}
      </div>

      <div className="section">
        <h2>Top conversazioni problematiche</h2>
        {topProblematic.length === 0 ? (
          <div className="empty-state">Nessuna conversazione segnalata come problematica.</div>
        ) : (
          <ul className="problematic-list">
            {topProblematic.map((c) => (
              <li
                key={c.id}
                onClick={() => navigate(`/admin/monitoring/conversations/${c.id}`)}
              >
                <div>
                  <div>
                    <span className="badge channel">
                      {CHANNEL_LABELS[c.channel] || c.channel}
                    </span>{' '}
                    <strong>{c.contact_name || c.contact_id}</strong>
                  </div>
                  <div className="meta">
                    {c.problematic_reasons?.map((r) => (
                      <span className="badge" key={r} style={{ marginRight: 4, background: '#fff', border: '1px solid #c14545', color: '#c14545' }}>
                        {r}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="meta">
                  {c.last_message_at
                    ? new Date(c.last_message_at).toLocaleString('it-IT')
                    : '—'}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="section">
        <h2>Manutenzione</h2>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button
            className="btn"
            onClick={handleBackfill}
            disabled={backfillBusy}
          >
            {backfillBusy ? 'Avvio…' : 'Avvia backfill sentiment'}
          </button>
          <span style={{ fontSize: 12, color: '#666' }}>
            Ricalcola il sentiment per i messaggi storici privi di analisi.
            Eseguibile più volte: ogni invocazione processa fino a 500 righe.
          </span>
        </div>
      </div>

      {loading && <div style={{ marginTop: 12, color: '#666' }}>Aggiornamento…</div>}
    </div>
  );
}
